import { useEffect, useRef, useState } from "react"
import { AnalysisSummary } from "../../../types"
import { KDropdown, KSpan, KTitleSpan } from "kahuna-base-react-components"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { MultiValue } from "react-select"
import { Line } from "react-chartjs-2"
import {
  addCommasToNumber,
  fillDatesBetween,
  formatDateObject,
  formatNumber,
  getCurrency,
  getSummaries,
  mapListToCamelCase
} from "../../../utility"
import ChartDataLabels from "chartjs-plugin-datalabels"
import { Chart as ChartJS, registerables, LineElement, ChartOptions } from "chart.js"
import { externalTooltipHandler } from "./externalTooltip"
import { DateRangeType } from "kahuna-base-react-components/dist/components/KSelectRange/KSelectRange"
import { lang } from "../../../constants/languages"

ChartJS.register(...registerables, ChartDataLabels, LineElement)

const rangeOptions: KSelectOption[] = [
  { label: "3 months", value: 2 },
  { label: "6 months", value: 5 },
  { label: "12 months", value: 11 },
  { label: "All times", value: -1 }
]

export interface ChartData {
  /*totalNetDatas: {
    date: string
    totalNet: number
    labelSpecificTotalNet: {
      label: string
      totalNet: number
    }[]
  }[]*/
  streamDatas: {
    date: string
    stream: number
    labelSpecificStream: {
      label: string
      stream: number
    }[]
  }[]
}

const AnalysisLineChart = ({
  data,
  setData,
  currency,
  dateValues,
  platformOptions,
  loading,
  selectedSubOptions,
  setSelectedSubOptions
}: {
  data: AnalysisSummary[]
  setData: (data: AnalysisSummary[]) => void
  currency: string
  dateValues: DateRangeType
  platformOptions: KSelectOption[]
  loading: boolean
  selectedSubOptions: KSelectOption | MultiValue<KSelectOption> | undefined
  setSelectedSubOptions: (selectedSubOptions: KSelectOption | MultiValue<KSelectOption> | undefined) => void
}) => {
  const initialChartData: ChartData = {
    // totalNetDatas: [],
    streamDatas: []
  }

  const [totalValues, setTotalValues] = useState<{ stream: number }>({
    // revenue: 0,
    stream: 0
  })

  const [hoveredIndex, setHoveredIndex] = useState<{ datasetIndex: number; dataIndex: number }>({
    datasetIndex: -1,
    dataIndex: -1
  })
  //const [percentChange, setPercentChange] = useState<number | undefined>(undefined)
  const [percentChange, setPercentChange] = useState<{ revenue: number | undefined; stream: number | undefined }>({
    revenue: undefined,
    stream: undefined
  })

  const [chartData, setChartData] = useState<ChartData>(initialChartData)

  const didMountRef = useRef(false)
  useEffect(() => {
    const getAllSummary = async () => {
      if (!didMountRef?.current) {
        didMountRef.current = true
        return
      }
      if (dateValues && dateValues[0] && dateValues[1]) {
        let responseData
        const startDate = formatDateObject(dateValues[0])
        const endDate = formatDateObject(dateValues[1])

        if ((selectedSubOptions as KSelectOption[]).length === 0) {
          // This will show the specific month, not a range, so start and end will be the same.
          responseData = await getSummaries("date", "platform", "all_platforms", false, startDate, endDate)
        } else {
          const optionsArray = selectedSubOptions as KSelectOption[]
          const value = optionsArray.map((subOption) => subOption.value2).join(",")
          responseData = await getSummaries("date", "platform", value, false, startDate, endDate)
        }
        setData(mapListToCamelCase(responseData))
      }
    }
    getAllSummary().then(() => {})
  }, [selectedSubOptions, dateValues])

  useEffect(() => {
    if (hoveredIndex.dataIndex > 0) {
      // const currentMonthTotalNet = chartData?.totalNetDatas?.[hoveredIndex.dataIndex]?.totalNet
      // const previousMonthTotalNet = chartData?.totalNetDatas?.[hoveredIndex.dataIndex - 1]?.totalNet
      const currentMonthStream = chartData?.streamDatas?.[hoveredIndex.dataIndex]?.stream
      const previousMonthStream = chartData?.streamDatas?.[hoveredIndex.dataIndex - 1]?.stream

      /*if (currentMonthTotalNet !== null && currentMonthTotalNet !== undefined && previousMonthTotalNet) {
        const calculatedPercentChange = Number(
          (
            ((Number(currentMonthTotalNet) - Number(previousMonthTotalNet)) / Number(previousMonthTotalNet)) *
            100
          ).toFixed(0)
        )
        setPercentChange((prev) => ({ ...prev, revenue: calculatedPercentChange }))
      } else {
        setPercentChange((prev) => ({ ...prev, revenue: undefined }))
      } */

      if (currentMonthStream !== null && currentMonthStream !== undefined && previousMonthStream) {
        const calculatedPercentChange = Number(
          (((Number(currentMonthStream) - Number(previousMonthStream)) / Number(previousMonthStream)) * 100).toFixed(0)
        )
        setPercentChange((prev) => ({ ...prev, stream: calculatedPercentChange }))
      } else {
        setPercentChange((prev) => ({ ...prev, stream: undefined }))
      }
    } else {
      setPercentChange({ revenue: undefined, stream: undefined })
    }
  }, [hoveredIndex])

  useEffect(() => {
    if (data && dateValues && dateValues[0] && dateValues[1]) {
      const firstMonth = formatDateObject(dateValues[0])
      const lastMonth = formatDateObject(dateValues[1])
      const months = fillDatesBetween(firstMonth, lastMonth)
      const newDatasTotalNet: {
        date: string
        totalNet: number
        labelSpecificTotalNet: { label: string; totalNet: number }[]
      }[] = []
      const newDatasStream: {
        date: string
        stream: number
        labelSpecificStream: { label: string; stream: number }[]
      }[] = []
      //const platformSpecificValues: { [key: string]: number} = []
      months.forEach((month, index) => {
        const labelSpecificTotal: { label: string; totalNet: number }[] = []
        const labelSpecificStream: { label: string; stream: number }[] = []

        const result = data
          .filter((summary) => {
            if (summary.date === month) {
              const label = summary.label
              labelSpecificTotal.push({ label, totalNet: Number(summary.totalNet) })
              labelSpecificStream.push({ label, stream: summary.quantity })

              return true
            }
            return false
          })
          .reduce(
            (accumulator, currentValue) => {
              accumulator.totalNet += Number(currentValue.totalNet)
              accumulator.stream += currentValue.quantity

              return accumulator
            },
            { totalNet: 0, stream: 0 }
          )

        newDatasTotalNet.push({
          date: month,
          totalNet: result.totalNet || 0,
          labelSpecificTotalNet: labelSpecificTotal
        })
        newDatasStream.push({ date: month, stream: result.stream || 0, labelSpecificStream })
      })
      // const totalRevenue = newDatasTotalNet.reduce((sum, item) => sum + item.totalNet, 0)
      const totalStream = newDatasStream.reduce((sum, item) => sum + item.stream, 0)

      setChartData({ streamDatas: newDatasStream })
      setTotalValues({ stream: totalStream })
    }
  }, [data])

  const myChartData = {
    labels: chartData.streamDatas.map((data) => data?.date),
    datasets: [
      /*{
        label: lang.analytics.revenue,
        data: chartData.totalNetDatas.map((data) => Number(data.totalNet.toFixed(2))),
        borderColor: hoveredIndex.datasetIndex === 1 ? "#EBE0FF" : "#7C2DFC",
        backgroundColor: hoveredIndex.datasetIndex === 1 ? "#EBE0FF" : "#7C2DFC",
        borderWidth: 2,
        yAxisID: "y",
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHoverBackgroundColor: "#000",
        pointHoverBorderColor: "#FFF",
        order: hoveredIndex.datasetIndex === 1 ? 2 : 1,
        borderSkipped: false
      },*/
      {
        label: lang.analytics.stream,
        data: chartData.streamDatas.map((data) => data?.stream),
        borderColor: hoveredIndex.datasetIndex === 0 ? "#F7DADA" : "#FF58EC",
        backgroundColor: hoveredIndex.datasetIndex === 0 ? "#F7DADA" : "#FF58EC",
        yAxisID: "y1",
        pointRadius: 0,
        borderWidth: 2,
        pointHoverRadius: 5,
        pointHoverBackgroundColor: "#000",
        pointHoverBorderColor: "#FFF",
        order: hoveredIndex.datasetIndex === 0 ? 2 : 1,
        borderSkipped: false
      }
    ]
  }

  const options: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        align: "end",
        labels: {
          color: "#000",
          usePointStyle: true,
          pointStyle: "circle",
          font: {
            family: "Inter",
            weight: 500,
            size: 13,
            lineHeight: "13px"
          },
          boxWidth: 5,
          boxHeight: 5
        }
      },
      title: {
        display: false,
        text: "Line Chart with Two Datasets"
      },
      datalabels: {
        display: false
      },
      tooltip: {
        intersect: false,
        displayColors: false,
        enabled: false, // it makes the original tooltip appear or disappear
        padding: 12,
        external: (context) => externalTooltipHandler(context, chartData, currency),
        xAlign: "left"

        /* callbacks: {
          label: (tooltipItem) => {
            const value = tooltipItem.raw
            const datasetIndex = tooltipItem.datasetIndex
            if (datasetIndex === 0) {
              return `Revenue: ${value}`
            } else {
              return `Streams: ${addCommasToNumber(String(value))}`
            }
          }
        }*/
      }
    },
    hover: {
      intersect: false
    },
    scales: {
      x: {
        offset: true,
        display: false,
        border: {
          display: false
        },
        grid: {
          display: false
        }
      },
      y: {
        offset: true,
        display: false,
        border: {
          display: false
        },
        grid: {
          display: false
        }
      },
      y1: {
        offset: true,
        display: false,
        border: {
          display: false
        },
        grid: {
          display: false
        }
      }
    },
    onHover: (event, chartElements) => {
      if (chartElements.length > 0) {
        const element = chartElements[0]
        const hoveredDatasetIndex = element.datasetIndex
        const hoveredDataIndex = element.index
        if (hoveredDatasetIndex !== hoveredIndex.datasetIndex || hoveredDataIndex !== hoveredIndex.dataIndex) {
          setHoveredIndex({ datasetIndex: hoveredDatasetIndex, dataIndex: hoveredDataIndex })
        }
      } else {
        setHoveredIndex((prev) => {
          return { ...prev, datasetIndex: -1, dataIndex: -1 }
        })
      }
    }
  }

  return (
    <div
      className="flex flex-row h-[374px] w-full p-5"
      style={{
        borderRadius: "10px",
        border: "1px solid var(--stroke-soft-200, #F3F3F3)",
        background: "var(--bg-white-0, #FFF)",
        boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
      }}
    >
      <div className="flex flex-col justify-between w-[25%]">
        <div className="flex flex-row items-center p-3.5 gap-1 whitespace-nowrap">
          <img src="/analytics_icons/music-album.svg" />
          <KSpan text={lang.analytics.monthly_streams} color="#000" fontWeight={500} />
        </div>
        <div className="h-full flex flex-col gap-2.5 pl-3 justify-end">
          <div className="h-auto flex flex-col justify-start gap-1 p-0 ">
            <KSpan text={lang.analytics.streams} fontSize={12} />
            <div className="flex items-center">
              <KTitleSpan
                text={`${formatNumber(totalValues.stream)}`}
                fontSize={40}
                lineHeight="52px"
                letterSpacing="-0.48px"
                color="#000"
              />
              <div className="h-8 self-end ml-1">
                {percentChange?.stream ? (
                  <div className="flex flex-row items-center gap-1 whitespace-nowrap">
                    <img
                      src={
                        percentChange?.stream > 0 ? "/analytics_icons/caret-up.svg" : "/analytics_icons/caret-down.svg"
                      }
                      alt="change-icon"
                      height={16}
                      width={16}
                    />
                    <KSpan
                      text={`${percentChange?.stream}%`}
                      color={percentChange?.stream > 0 ? "#56E099" : "#FF5865"}
                    />
                    <KSpan
                      text={lang.analytics.last_month_comparison_desc}
                      color="#999"
                      fontSize={11}
                      lineHeight="16px"
                      fontWeight={500}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className="w-[75%] flex flex-col"
        onMouseLeave={() => {
          setHoveredIndex((prev) => {
            return { ...prev, datasetIndex: -1, dataIndex: -1 }
          })
        }}
      >
        <div className="h-12 flex flex-row justify-end items-start">
          {!loading && (
            <div className="flex items-center justify-center max-w-[350px]">
              <KDropdown
                options={platformOptions}
                isMulti
                onSelect={(elements) => {
                  // do nothing
                }}
                onBlur={(selected) => {
                  if (selected) {
                    setSelectedSubOptions(selected)
                  }
                }}
                placeholder={lang.analytics.all_platforms_placeholder}
                showOnlyIconsInMulti
                selected={selectedSubOptions}
                rightIcon="/analytics_icons/caret-down-new.svg"
                menuWidth={170}
                enableIndicator={true}
                placeholderColor="#000"
                closeMenuOnSelect={false}
              />
            </div>
          )}
        </div>
        <div
          className="flex-1 min-h-0"
          onMouseLeave={() => {
            setHoveredIndex((prev) => {
              return { ...prev, datasetIndex: -1, dataIndex: -1 }
            })
          }}
        >
          {chartData.streamDatas && <Line data={myChartData} options={options} />}
        </div>
      </div>
    </div>
  )
}

export default AnalysisLineChart
